"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchSkeleton } from "@/fetch-ui/FetchSkeleton";

import { useAirportCatalog } from "../../_hooks/useAirportCatalog";
import { farAirportNote, searchAirports } from "../../_logic/airport-options";
import { AIRPORT_PICKER_SKELETON_ROWS } from "../../_logic/constants";
import { PopoverSelect } from "../ui/PopoverSelect";

interface AirportPickerProps {
  /** The home city the airport is for. */
  city: string;
  /** The airport showing: the planner's pick, else the one fares came from, else null. */
  code: string | null;
  /** Whether the planner picked it (shown in indigo). */
  picked: boolean;
  onPick: (code: string) => void;
}

/**
 * Picks the airport a home city flies from: the city's own airports first (Tokyo: HND, NRT), and
 * a search over every scheduled airport for flying from somewhere else. Only a real airport can
 * be chosen; one that serves another city is kept but shown in amber with the reason. While the
 * catalog loads the list shows placeholder rows, and a failed load shows an error with a retry instead of
 * "No matches".
 * @param props - The city, its airport and the pick handler.
 * @returns The picker, a compact chip in the row.
 * @throws Never.
 */
export const AirportPicker = ({ city, code, picked, onPick }: AirportPickerProps) => {
  // Hooks
  const { catalog, status, retry } = useAirportCatalog(city);

  // Derived
  const own = catalog?.own ?? [];
  const far = code && catalog ? farAirportNote(code, city, own, catalog.airports) : null;
  const emptyState =
    status === "loading" ? (
      <div role="status" aria-label="Loading airports" className="flex flex-col gap-3 px-3 py-3">
        {Array.from({ length: AIRPORT_PICKER_SKELETON_ROWS }, (_, row) => (
          <div key={row} className="flex flex-col gap-1.5">
            <FetchSkeleton width="70%" height={12} />
            <FetchSkeleton width="40%" height={10} />
          </div>
        ))}
      </div>
    ) : status === "error" ? (
      <div role="alert" className="flex flex-col items-center gap-2 px-3 py-5">
        <p className="text-sm font-medium text-gray-600">Couldn&apos;t load airports</p>
        <FetchButton variant="outline" size="xs" onClick={retry}>
          Retry
        </FetchButton>
      </div>
    ) : undefined;

  // Render
  return (
    <span title={far ?? undefined} className={far ? "text-amber-700" : undefined}>
      <PopoverSelect
        ariaLabel={`Airport for ${city}`}
        searchPlaceholder="Search any airport"
        options={(query) => {
          const found = query ? searchAirports(query, catalog?.airports ?? []) : own;
          return found.map((option) => ({ value: option.code, label: option.label, sublabel: option.sublabel }));
        }}
        value={code}
        onChange={onPick}
        emptyState={emptyState}
        trigger={{ compact: true, accent: picked && !far, label: code ?? "airport" }}
      />
    </span>
  );
};
